import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Loader2, Save, Play, RotateCcw, AlertCircle, CheckCircle2, FileText } from 'lucide-react';

const REPORT_TYPES = [
  { value: 'weekly', label: 'Weekly Progress' },
  { value: 'sprint_summary', label: 'Sprint Summary' },
  { value: 'stakeholder_update', label: 'Stakeholder Update' },
  { value: 'team_sprint_report', label: 'Team Sprint Report' },
  { value: 'executive', label: 'Executive Update' },
  { value: 'release_notes', label: 'Release Notes' },
];

const AUDIENCES = [
  { value: 'executive', label: 'Executive' },
  { value: 'stakeholder', label: 'Stakeholder' },
  { value: 'technical', label: 'Technical' },
  { value: 'customer', label: 'Customer' },
];

const KNOWN_VARIABLES = ['report_type', 'audience', 'sprint_name', 'product_name'];

// Renders just the editor body — used as the "Templates" tab inside Reports.js.
// The standalone page wrapper below also exports this for `/reports/templates`
// (kept as a redirect target — see App.js).
export function ReportTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [statusToast, setStatusToast] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/report-templates');
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setTemplates(list);
        if (list.length && !selectedId) {
          setSelectedId(list[0].id);
          setDraft({ ...list[0] });
        }
      } catch (err) {
        setError(err?.response?.data?.detail || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  const onSelect = (tmpl) => {
    setSelectedId(tmpl.id);
    setDraft({ ...tmpl });
    setTestResult(null);
  };

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    return (
      draft.system_prompt_template !== selected.system_prompt_template ||
      draft.name !== selected.name ||
      draft.description !== selected.description ||
      draft.active !== selected.active
    );
  }, [selected, draft]);

  const onSave = async () => {
    if (!draft || !dirty) return;
    setSaving(true);
    setStatusToast('');
    try {
      const res = await api.patch(`/report-templates/${draft.id}`, {
        name: draft.name,
        description: draft.description,
        system_prompt_template: draft.system_prompt_template,
        active: draft.active,
      });
      const updated = res.data;
      setTemplates((list) => list.map((t) => (t.id === updated.id ? updated : t)));
      setDraft({ ...updated });
      setStatusToast('Saved.');
      setTimeout(() => setStatusToast(''), 2000);
    } catch (err) {
      setStatusToast(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => {
    if (selected) setDraft({ ...selected });
    setTestResult(null);
  };

  const onTestGenerate = async () => {
    if (!draft) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/report-templates/test-generate', {
        report_type: draft.report_type,
        audience: draft.audience || 'stakeholder',
        template_id: draft.id,
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        success: false,
        error: err?.response?.data?.detail || 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const insertVariable = (varName) => {
    if (!draft) return;
    const insert = `{{${varName}}}`;
    setDraft({ ...draft, system_prompt_template: (draft.system_prompt_template || '') + insert });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-[#98139C]" size={32} />
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-[#30302F]/70 dark:text-white/70 mb-6">
        Author the system prompts that AI uses to generate each report. Default templates can't be deleted; deactivate them instead.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-200 flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Template list */}
        <aside className="col-span-4 lg:col-span-3">
          <div className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl divide-y divide-[#e3e3e3] dark:divide-white/10">
            {templates.length === 0 && (
              <div className="p-4 text-sm text-[#30302F]/60 dark:text-white/50">No templates yet.</div>
            )}
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => onSelect(t)}
                className={`w-full text-left px-4 py-3 hover:bg-[#faf8fb] dark:hover:bg-white/5 ${
                  selectedId === t.id ? 'bg-[#f0dcf0] dark:bg-[#98139C]/10' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[#98139C] dark:text-[#d87bdb]" />
                  <span className="text-sm font-semibold text-[#30302F] dark:text-white truncate">
                    {t.name}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-[#30302F]/60 dark:text-white/50">
                  <span className="capitalize">{t.report_type.replace('_', ' ')}</span>
                  <span aria-hidden>·</span>
                  <span className="capitalize">{t.audience || 'any audience'}</span>
                  {t.is_default && (
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-[#98139C] dark:text-[#d87bdb]">
                      Default
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <main className="col-span-8 lg:col-span-9">
          {!draft ? (
            <div className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-8 text-center text-sm text-[#30302F]/60 dark:text-white/50">
              Pick a template to edit.
            </div>
          ) : (
            <div className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="w-full text-lg font-bold bg-transparent border-b border-[#e3e3e3] dark:border-white/10 focus:border-[#98139C] dark:focus:border-[#d87bdb] focus:outline-none text-[#30302F] dark:text-white pb-1"
                    placeholder="Template name"
                  />
                  <p className="mt-1 text-xs text-[#30302F]/60 dark:text-white/50">
                    {REPORT_TYPES.find((r) => r.value === draft.report_type)?.label || draft.report_type}
                    {draft.audience && ` · ${AUDIENCES.find((a) => a.value === draft.audience)?.label || draft.audience}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#30302F]/70 dark:text-white/70">
                    <input
                      type="checkbox"
                      checked={!!draft.active}
                      onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                      className="rounded accent-[#98139C]"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70 mb-1.5 block">
                  Description
                </label>
                <input
                  type="text"
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-[#1a1a2e] text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C] dark:focus:border-[#d87bdb]"
                  placeholder="Short description for other admins"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70">
                    System Prompt
                  </label>
                  <div className="flex gap-1.5">
                    {KNOWN_VARIABLES.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => insertVariable(v)}
                        className="px-2 py-0.5 text-[11px] font-mono rounded bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] hover:bg-[#98139C]/30"
                      >
                        + {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  value={draft.system_prompt_template || ''}
                  onChange={(e) => setDraft({ ...draft, system_prompt_template: e.target.value })}
                  className="w-full min-h-[360px] px-3 py-2 text-sm font-mono rounded-md border border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-[#0f0f1c] text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C] dark:focus:border-[#d87bdb]"
                  placeholder="You are a PM assistant... Respond with JSON only."
                />
                <p className="mt-1 text-xs text-[#30302F]/60 dark:text-white/50">
                  Placeholders: <code className="font-mono">{`{{report_type}}`}</code>{' '}
                  <code className="font-mono">{`{{audience}}`}</code>{' '}
                  <code className="font-mono">{`{{sprint_name}}`}</code>{' '}
                  <code className="font-mono">{`{{product_name}}`}</code>
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#e3e3e3] dark:border-white/10">
                <div className="flex gap-2">
                  <button
                    onClick={onSave}
                    disabled={!dirty || saving}
                    className="inline-flex items-center gap-1.5 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                  </button>
                  <button
                    onClick={onRevert}
                    disabled={!dirty}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#30302F]/70 dark:text-white/70 px-3 h-9 rounded-full hover:bg-[#faf8fb] dark:hover:bg-white/5 disabled:opacity-40"
                  >
                    <RotateCcw size={14} /> Revert
                  </button>
                </div>
                <button
                  onClick={onTestGenerate}
                  disabled={testing}
                  className="inline-flex items-center gap-1.5 border border-[#98139C] text-[#98139C] dark:text-[#d87bdb] dark:border-[#d87bdb] hover:bg-[#f0dcf0] dark:hover:bg-[#98139C]/10 rounded-full font-bold text-sm px-5 h-9"
                >
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Test generate
                </button>
              </div>

              {statusToast && (
                <div className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                  <CheckCircle2 size={14} /> {statusToast}
                </div>
              )}

              {testResult && (
                <div className="pt-3 border-t border-[#e3e3e3] dark:border-white/10">
                  <h3 className="text-sm font-semibold text-[#30302F] dark:text-white mb-2">
                    Test result {testResult.latency_ms ? `(${testResult.latency_ms}ms)` : ''}
                  </h3>
                  {testResult.success === false && (
                    <p className="text-sm text-red-700 dark:text-red-300">{testResult.error}</p>
                  )}
                  {testResult.parse_error && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                      Model output was not valid JSON — your template may need stronger instructions.
                    </p>
                  )}
                  <pre className="text-xs font-mono bg-[#faf8fb] dark:bg-[#0f0f1c] border border-[#e3e3e3] dark:border-white/10 rounded p-3 overflow-x-auto max-h-80 text-[#30302F] dark:text-white whitespace-pre-wrap">
                    {testResult.content
                      ? JSON.stringify(testResult.content, null, 2)
                      : (testResult.raw_response || '(no output)')}
                  </pre>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
